import { Icon, Text } from '@blueprintjs/core'
import { MenuItem } from '../ui/menu'
import classNames from 'classnames'
import deepEqual from 'fast-deep-equal'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '../../hooks/useUser'
import type {
    AdGroupSelector,
    AdSelector,
    CampaignSelector,
    PlacementSelector,
    PortfolioSelector,
    TargetSelector,
} from '../../types/filter'
import { axiosGet } from '../../util/api'
import { Popover } from '../ui/popover'

type ClassValue = classNames.Argument

interface BaseSelectionProps<
    T extends
        | PortfolioSelector
        | CampaignSelector
        | AdGroupSelector
        | TargetSelector
        | AdSelector
        | PlacementSelector,
> {
    value: T
    onChange: (v: T) => any
    endpoint: string
    primaryKey:
        | 'portfolio_id'
        | 'campaign_id'
        | 'ad_group_id'
        | 'target_id'
        | 'ad_id'
        | 'placement_id'
    defaultText: string
    className?: ClassValue
    disabled?: boolean
    raw?: boolean
}

export const PortfolioSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: PortfolioSelector
    onChange: (v: PortfolioSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => {
    return (
        <BaseSelection
            className={className}
            disabled={disabled}
            value={value}
            onChange={onChange}
            endpoint="amazon-portfolios/search"
            primaryKey="portfolio_id"
            defaultText="Select a portfolio"
        />
    )
}

export const CampaignSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: CampaignSelector
    onChange: (v: CampaignSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => {
    return (
        <BaseSelection
            className={className}
            disabled={disabled}
            value={value}
            onChange={onChange}
            endpoint="api/v5/campaigns/search"
            primaryKey="campaign_id"
            defaultText="Select a campaign"
            raw
        />
    )
}

export const AdGroupSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: AdGroupSelector
    onChange: (v: AdGroupSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => {
    return (
        <BaseSelection
            className={className}
            disabled={disabled}
            value={value}
            onChange={onChange}
            endpoint="api/v5/ad-groups/search"
            primaryKey="ad_group_id"
            defaultText="Select an ad group"
            raw
        />
    )
}

export const PlacementSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: PlacementSelector
    onChange: (v: PlacementSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => {
    return (
        <BaseSelection
            className={className}
            disabled={disabled}
            value={value}
            onChange={onChange}
            endpoint="placements/search"
            primaryKey="placement_id"
            defaultText="Select a Placement"
        />
    )
}

export const TargetSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: TargetSelector
    onChange: (v: TargetSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => (
    <BaseSelection
        className={className}
        disabled={disabled}
        value={value}
        onChange={onChange}
        endpoint="api/v5/targets/search"
        primaryKey="target_id"
        defaultText="Select a keyword or target"
        raw
    />
)

export const AdSelection = ({
    value,
    onChange,
    className,
    disabled,
}: {
    value: AdSelector
    onChange: (v: AdSelector) => any
    className?: ClassValue
    disabled?: boolean
}): JSX.Element => (
    <BaseSelection
        className={className}
        disabled={disabled}
        value={value}
        onChange={onChange}
        endpoint="api/v5/product-ads/search"
        primaryKey="ad_id"
        defaultText="Select an ad"
        raw
    />
)

const BaseSelection = <
    T extends
        | PortfolioSelector
        | CampaignSelector
        | AdGroupSelector
        | TargetSelector
        | AdSelector
        | PlacementSelector,
>({
    value,
    onChange,
    endpoint,
    primaryKey,
    defaultText,
    className,
    disabled,
    raw = false,
}: BaseSelectionProps<T>): JSX.Element => {
    const { activeProfile, profile } = useUser()
    const selectorScope = `${profile ?? 'missing'}:${activeProfile?.profile_id ?? 'missing'}:${endpoint}`
    const [loading, setLoading] = useState(false)
    const [count, setCount] = useState(0)
    const [items, setItems] = useState<T[]>([])
    const [selectedState, setSelectedState] = useState<{
        scope: string
        value: T
    } | null>(null)
    const selected = selectedState?.scope === selectorScope ? selectedState.value : null
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null)
    const activeSelectorScope = useRef(selectorScope)
    const latestLoadRequest = useRef(0)
    activeSelectorScope.current = selectorScope

    useEffect(() => {
        if (!deepEqual(value, { ...selected, type: 'select-component' })) {
            setSelectedState({ scope: selectorScope, value })
        }
    }, [selectorScope, value])

    useEffect(() => {
        if (selected) {
            onChange({
                ...selected,
                type: 'select-component',
            })
        }
    }, [selected])

    const load = useCallback(
        (query?: string) => {
            const requestedScope = selectorScope
            const request = ++latestLoadRequest.current
            const requestIsCurrent = () =>
                request === latestLoadRequest.current &&
                requestedScope === activeSelectorScope.current
            setLoading(true)
            axiosGet(endpoint, { query }, raw)
                .then((res) => {
                    if (!requestIsCurrent()) return
                    setItems(res.data.data)
                    setCount(res.data.total)
                    setLoading(false)
                })
                .catch(() => {
                    if (requestIsCurrent()) setLoading(false)
                })
        },
        [endpoint, raw, selectorScope],
    )

    const search = useMemo(
        () =>
            debounce((query) => {
                load(query)
            }, 150),
        [load],
    )

    useEffect(() => () => search.cancel(), [search])

    useEffect(() => {
        setQuery('')
        setOpen(false)
        setItems([])
        setCount(0)
        setLoading(false)
        if (!profile) {
            return
        }

        load()
        return () => {
            latestLoadRequest.current++
        }
    }, [profile, selectorScope, load])

    const handleQueryChange = (value: string) => {
        setQuery(value)
        search(value)
    }

    const selectItem = (item: T, event?: React.MouseEvent<HTMLAnchorElement>) => {
        event?.preventDefault()
        event?.stopPropagation()
        event?.nativeEvent.stopImmediatePropagation()

        if ((item as any).__disabled) {
            return
        }

        setSelectedState({ scope: selectorScope, value: item })
        setOpen(false)
    }

    const renderItemText = (item: T) => {
        let text: any

        if ('context' in item && item.context) {
            text = (
                <div>
                    <div>{highlightText(item.name, query)}</div>
                    <div className="text-xs text-gray-500">
                        {highlightText(item.context, query)}
                    </div>
                </div>
            )
        } else if ('campaign_name' in item && item.campaign_name) {
            const cname = item.campaign_name
            text = (
                <div>
                    <div>Campaign: {highlightText(cname, query)}</div>
                    <div>Ad Group: {highlightText(item.name, query)}</div>
                </div>
            )
        } else {
            text = highlightText(item.name, query)
        }

        return text
    }

    const itemsWithLast: T[] = useMemo(() => {
        if (items.length == count) {
            return items
        } else {
            // @ts-expect-error
            const n: T = {
                [primaryKey]: null,
                name: `...and ${count - items.length} more not shown`,
                __disabled: true,
            }
            return [...items, n]
        }
    }, [items, count])

    const content = (
        <div className="w-[276px] py-1">
            <div className="px-2 pb-1">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)}
                    placeholder="Search..."
                    className="w-full rounded-md border border-[#DBE2E8] bg-white px-2 py-1 text-[12px] text-[#0E2947] placeholder-[#94A3B8] outline-none focus:border-[#3391FF] focus:ring-1 focus:ring-[#3391FF]"
                    autoFocus
                />
            </div>
            <div className="max-h-60 overflow-y-auto">
                {itemsWithLast.length === 0 ? (
                    <MenuItem disabled text={loading ? 'Loading...' : 'No results.'} />
                ) : (
                    itemsWithLast.map((item) => {
                        const isSyntheticDisabledItem = Boolean((item as any).__disabled)
                        const itemKey = (item as any)[primaryKey] ?? `disabled-${primaryKey}`

                        return (
                            <MenuItem
                                key={itemKey}
                                selected={deepEqual(selected, item)}
                                disabled={isSyntheticDisabledItem}
                                shouldDismissPopover={false}
                                onClick={(event) => selectItem(item, event)}
                                text={renderItemText(item)}
                            />
                        )
                    })
                )}
            </div>
        </div>
    )

    return (
        <div ref={setPortalContainer} className={classNames('relative mt-2', className)}>
            <Popover
                isOpen={open}
                onClose={() => setOpen(false)}
                disabled={disabled}
                content={content}
                position="bottom-left"
                minimal
                targetTagName="div"
                portalContainer={portalContainer ?? undefined}
            >
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen((prev) => !prev)}
                    className="inline-flex w-full max-w-[276px] min-h-[30px] min-w-[30px] items-center justify-between rounded-md border border-[rgba(28,33,39,0.2)] bg-white px-[10px] py-[5px] text-left text-sm font-normal text-[#1c2127] transition-colors hover:bg-[#f6f7f9] active:bg-[#edeff2] disabled:pointer-events-none disabled:opacity-50"
                >
                    <Text ellipsize className="max-w-[238px] flex-1">
                        {selected?.name || defaultText}
                    </Text>
                    <Icon icon="caret-down" className="ml-2 shrink-0" />
                </button>
            </Popover>
        </div>
    )
}

function escapeRegExpChars(text: string) {
    return text.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1')
}

function highlightText(text: string, query: string) {
    let lastIndex = 0
    const words = query
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map(escapeRegExpChars)
    if (words.length === 0) {
        return [text]
    }
    const regexp = new RegExp(words.join('|'), 'gi')
    const tokens: React.ReactNode[] = []
    while (true) {
        const match = regexp.exec(text)
        if (!match) {
            break
        }
        const length = match[0].length
        const before = text.slice(lastIndex, regexp.lastIndex - length)
        if (before.length > 0) {
            tokens.push(before)
        }
        lastIndex = regexp.lastIndex
        tokens.push(<strong key={lastIndex}>{match[0]}</strong>)
    }
    const rest = text.slice(lastIndex)
    if (rest.length > 0) {
        tokens.push(rest)
    }
    return tokens
}
